import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../api/client';
import { ProductCatalogDto, ProductDetailDto, CompanyDto } from '../types/dtos';
import LoadingSpinner from '../components/LoadingSpinner';
import { Plus } from 'lucide-react';
import AddProductModal from '../components/AddProductModal'; // 1. Import Modal
import { resolveUrl } from '../utils/urlUtils';

export default function Products() {
  const navigate = useNavigate();
  const [products, setProducts] = useState<ProductCatalogDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('All Products');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<ProductDetailDto | null>(null);

  const [companies, setCompanies] = useState<CompanyDto[]>([]); // ✅ Added
  const [isSuperAdmin, setIsSuperAdmin] = useState(false); // ✅ Added
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>(''); // ✅ Added filter state

  // 3. Define fetch as a reusable function
  const fetchProducts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.get<ProductCatalogDto[]>('/admin/products');
      setProducts(res.data);
      setError(null);
    } catch (err: any) {
      console.error('[Products] Failed to load catalog:', err);
      setError(err.response?.data?.message || 'Unable to load products. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      // 1. Check if Super Admin & Load companies
      try {
        const profileRes = await apiClient.get<any>('/admin/profile');
        const isSuper = profileRes.data.company === 'Xtrem Group';
        setIsSuperAdmin(isSuper);
        if (isSuper) {
          const companiesRes = await apiClient.get<CompanyDto[]>('/admin/companies');
          setCompanies(companiesRes.data);
        }
      } catch (err) {
        console.error('[Products] Init profile/companies failed:', err);
      }
      // 2. Load products
      fetchProducts();
    };
    init();
  }, [fetchProducts]);

  const handleEditClick = async (productId: string) => {
    setLoading(true);
    try {
      const res = await apiClient.get<ProductDetailDto>(`/admin/products/${productId}`);
      setSelectedProduct(res.data);
      setIsAddModalOpen(true);
    } catch (err) {
      console.error('[Products] Failed to fetch product details:', err);
      setError('Failed to load product details for editing.');
    } finally {
      setLoading(false);
    }
  };

  const filteredProducts = products.filter(p => {
    // Tab Filter
    const matchesTab = activeTab === 'All Products' || p.type === activeTab;
    // Company Filter (For Super Admin view)
    const matchesCompany = !selectedCompanyId ||
      (selectedCompanyId === 'null' ? !p.companyId : p.companyId === selectedCompanyId);

    return matchesTab && matchesCompany;
  });

  return (
    <div className="w-full">
      <div className="max-w-7xl mx-auto pt-6 pb-8">

          {/* ... Breadcrumb and Stats Cards (Unchanged) ... */}
          {/* Breadcrumb */}
          <nav className="mb-6 text-sm text-gray-500" aria-label="Breadcrumb">
            <ol className="list-none p-0 inline-flex">
              <li className="flex items-center">
                <a href="/dashboard" className="text-blue-600 hover:underline">Home</a>
                <svg className="fill-current w-3 h-3 mx-3" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M285.476 272.971L91.132 467.314c-9.373 9.373-24.569 9.373-33.941 0l-22.667-22.667c-9.357-9.357-9.375-24.522-.04-33.901L188.505 256 34.484 101.255c-9.335-9.379-9.317-24.544.04-33.901l22.667-22.667c9.373-9.373 24.569-9.373 33.941 0L285.475 239.03c9.373 9.372 9.373 24.568.001 33.941z" />
                </svg>
              </li>
              <li>
                <span className="text-gray-500">All Products</span>
              </li>
            </ol>
          </nav>

          <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6 mb-6">
            <div className="flex flex-col lg:flex-row justify-between items-start gap-6">
              <div className="w-full lg:w-auto">
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Xtrem ONE Products</h1>
                <p className="mt-1 text-sm text-gray-500">Manage your product catalog</p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 sm:gap-8 w-full lg:w-auto">
                <div className="flex flex-col lg:items-end">
                  <div className="text-[10px] sm:text-xs text-gray-400 uppercase font-bold tracking-wider">Total Products</div>
                  <div className="text-lg sm:text-xl font-bold text-gray-900">{products.length}</div>
                </div>
                <div className="flex flex-col lg:items-end sm:border-l sm:border-gray-100 sm:pl-8">
                  <div className="text-[10px] sm:text-xs text-gray-400 uppercase font-bold tracking-wider">Categories</div>
                  <div className="text-lg sm:text-xl font-bold text-gray-900">
                    {Array.from(new Set(products.map(p => p.type))).length}
                  </div>
                </div>
                <div className="flex flex-col lg:items-end sm:border-l sm:border-gray-100 sm:pl-8 col-span-2 sm:col-span-1">
                  <div className="text-[10px] sm:text-xs text-gray-400 uppercase font-bold tracking-wider">Active Offers</div>
                  <div className="text-lg sm:text-xl font-bold text-gray-900">
                    {products.filter(p => p.status === 'Active').length}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6 mb-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex items-center gap-2 overflow-x-auto no-scrollbar pb-2 md:pb-0">
                {['All Products', 'Insurance', 'Pension', 'Banking'].map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${activeTab === tab
                      ? 'bg-blue-600 text-white shadow-md'
                      : 'text-gray-600 bg-gray-50 hover:bg-gray-100'
                      }`}
                  >
                    {tab}
                  </button>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                {/* Company Filter Dropdown (Super Admin Only) */}
                {isSuperAdmin && (
                  <select
                    className="flex-1 sm:flex-initial px-3 py-2 rounded-lg text-sm font-medium border-gray-300 border bg-white focus:ring-2 focus:ring-blue-500 outline-none shadow-sm"
                    value={selectedCompanyId}
                    onChange={(e) => setSelectedCompanyId(e.target.value)}
                  >
                    <option value="">All Companies (Catalog)</option>
                    <option value="null">Global Only</option>
                    {companies.map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                )}

                {/* Gated Add Button */}
                {isSuperAdmin && (
                  <button
                    onClick={() => {
                      setSelectedProduct(null);
                      setIsAddModalOpen(true);
                    }}
                    className="flex-1 sm:flex-initial px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 flex items-center justify-center shadow-lg transition-all active:scale-95"
                  >
                    <Plus size={18} className="mr-1.5" />
                    <span>Add New Policy</span>
                  </button>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {loading ? (
                <div className="col-span-3 flex justify-center items-center min-h-[200px]">
                  <LoadingSpinner />
                </div>
              ) : error ? (
                <div className="col-span-3 bg-red-50 border-l-4 border-red-500 p-4 text-red-700">
                  {error}
                </div>
              ) : filteredProducts.length === 0 ? (
                <div className="col-span-3 text-center py-10">
                  <p className="text-gray-600">No products found in this category.</p>
                  <button
                    onClick={() => setIsAddModalOpen(true)}
                    className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
                  >
                    Create First Product
                  </button>
                </div>
              ) : (
                filteredProducts.map((product) => (
                  <div
                    key={product.productId}
                    className="border border-gray-200 rounded-lg p-6 bg-white shadow-sm hover:shadow-md transition-shadow flex flex-col justify-between"
                  >
                    <div>
                      {/* Product Image */}
                      <div className="h-48 bg-gray-200 rounded-lg mb-4 flex items-center justify-center overflow-hidden">
                        {product.imageUrl ? (
                          <img
                            src={resolveUrl(product.imageUrl)}
                            alt={product.name}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              (e.target as HTMLImageElement).src = 'https://via.placeholder.com/400x200?text=Xtrem+One';
                            }}
                          />
                        ) : (
                          <span className="text-gray-400">No Image</span>
                        )}
                      </div>

                      {/* Product Info */}
                      <div className="flex justify-between items-start mb-2">
                        <div className="inline-block bg-blue-100 text-blue-800 text-xs font-medium px-2 py-1 rounded-full">
                          {product.type}
                        </div>
                        {product.status === 'Inactive' && (
                          <span className="inline-block bg-red-100 text-red-800 text-xs font-medium px-2 py-1 rounded-full">
                            Inactive
                          </span>
                        )}
                        {product.isDiscontinued && (
                          <span className="inline-block bg-orange-100 text-orange-800 text-xs font-medium px-2 py-1 rounded-full">
                            Discontinued
                          </span>
                        )}
                      </div>

                      <h3 className="font-semibold text-lg text-gray-900">{product.name}</h3>
                      <p className="text-xs text-gray-500 font-medium mb-1 truncate">
                        Owned by: <span className="text-blue-600">{product.companyName || 'Global'}</span>
                      </p>
                      {/* Note: The description wasn't in the original DTO but if backend sends it, we can show it */}
                      <p className="mt-2 text-sm text-gray-600 line-clamp-2">
                        {product.description || "No description available."}
                      </p>
                    </div>

                    <div className="mt-4 pt-4 border-t border-gray-100">
                      <div className="text-xl font-bold text-gray-900">
                        MK {(() => {
                          const amount = product.premiumFrequency === 'Once-off' || product.premiumFrequency === 'OnceOff'
                            ? (product.onceOffPremium || product.monthlyPremium || 0)
                            : (product.monthlyPremium || 0);
                          return amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                        })()}
                        <span className="text-sm font-normal text-gray-500">
                          {product.premiumFrequency === 'Once-off' || product.premiumFrequency === 'OnceOff' ? ' Once-off' : ' /mo'}
                        </span>
                      </div>
                    </div>

                    {/* Gated Edit Button */}
                    {isSuperAdmin && (
                      <button
                        onClick={() => handleEditClick(product.productId)}
                        className="mt-2 text-sm text-blue-600 hover:text-blue-800 font-semibold underline decoration-blue-200 hover:decoration-blue-600 transition-all"
                      >
                        Edit Details →
                      </button>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
      </div>

      {/* 5. Render Modal */}
      <AddProductModal
        isOpen={isAddModalOpen}
        onClose={() => {
          setIsAddModalOpen(false);
          setSelectedProduct(null);
        }}
        product={selectedProduct}
        onSuccess={() => {
          fetchProducts();
        }}
      />
    </div>
  );
}